<script lang="ts" setup>
import MediaForm from '@/components/media/MediaForm.vue';
import MediaItem from '@/components/media/MediaItem.vue';
import { Media, MediaPurpose } from '@/models/media.model';
import { Event, useEvents } from '@/services/events.composable';
import { Mutations, ShiftCMSState } from '@/store';
import { markRaw, PropType, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState>();

const props = defineProps({
  modelValue: {
    type: Object as PropType<{ media: Media[] }>,
    required: true,
  },
  required: {
    type: Array as PropType<MediaPurpose[]>,
    default: () => [],
  },
  top: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});

const requiresSave = ref<boolean>(false);

const events = useEvents();
const emit = defineEmits(['update:modelValue', 'change']);

const useMedia = (m: Media) => {
  emit('update:modelValue', {
    ...props.modelValue,
    media: [...(props.modelValue.media || []), m],
  });

  requiresSave.value = true;

  emit('change');

  events.off(Event.UseMedia, useMedia);
  store.commit(props.top ? Mutations.HideDrawer : Mutations.HideSubDrawer);
};

const updateMedia = (m: Media) => {
  emit('update:modelValue', {
    ...props.modelValue,
    media: [
      ...(props.modelValue.media || []).filter((me) => me.id !== m.id),
      m,
    ],
  });

  requiresSave.value = true;

  emit('change');

  events.off(Event.UpdateMedia, useMedia);
  store.commit(props.top ? Mutations.HideDrawer : Mutations.HideSubDrawer);
};

const removeMedia = (m: Media) => {
  emit('update:modelValue', {
    ...props.modelValue,
    media: [...(props.modelValue.media || []).filter((me) => me.id !== m.id)],
  });

  requiresSave.value = true;
  emit('change');
};

const viewMedia = (m: Media) => {
  window.open(`/file/${m.file?.uuid}/large.webp`, 'new');
};

const updateSaved = () => (requiresSave.value = false);

const editMedia = (m?: Media) => {
  events.on(Event.Saved, updateSaved);
  events.on(Event.UseMedia, useMedia);
  events.on(Event.UpdateMedia, updateMedia);
  store.commit(props.top ? Mutations.ShowDrawer : Mutations.ShowSubDrawer, {
    component: markRaw(MediaForm),
    data: { media: m },
  });
};

onUnmounted(() => {
  events.off(Event.Saved, updateSaved);
  events.off(Event.UseMedia, useMedia);
  events.off(Event.UpdateMedia, updateMedia);
});
</script>
<template>
  <div style="margin: 1em 0">
    <h3>{{ $t('initiative.edit.media') }}</h3>
    <v-alert
      density="compact"
      type="warning"
      class="mb-4 mt-4"
      v-if="
        props.required.filter(
          (v) => !props.modelValue.media?.map((m) => m.purpose).includes(v),
        ).length
      "
    >
      {{
        $t('initiative.edit.mediaRequired', {
          list: props.required
            .filter(
              (v) => !props.modelValue.media?.map((m) => m.purpose).includes(v),
            )
            .map((v) =>
              $t(`initiative.edit.image_${v.replace('-image', '_image')}`),
            )
            .join(', '),
        })
      }}
    </v-alert>
    <v-alert
      density="compact"
      type="info"
      class="mb-4 mt-4"
      v-if="props.modelValue.media?.length === 0"
    >
      {{ $t('initiative.edit.mediaNone') }}
    </v-alert>
    <v-alert
      density="compact"
      type="info"
      class="mb-4 mt-4"
      v-if="requiresSave"
    >
      {{ $t('initiative.edit.mediaSave') }}
    </v-alert>
    <div class="images">
      <MediaItem
        :media="m"
        v-for="m in props.modelValue.media"
        :key="m.id"
        :show-options="['edit', 'remove']"
        :showInfo="true"
        :disabled="disabled"
        @edit="editMedia(m)"
        @remove="removeMedia(m)"
        @view="viewMedia(m)"
      />
    </div>
    <v-btn v-if="!disabled" @click="editMedia()">{{
      $t('initiative.edit.mediaEdit')
    }}</v-btn>
  </div>
</template>
<style scoped>
.images {
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
}
.images > * {
  width: calc(50% - 1em);
  margin: 0.25em;
}
</style>
